import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { motion, AnimatePresence } from 'motion/react';
import { MLTLabTestRecord } from './mltLabTestData';

export interface PlateObservationState {
  plateNo: number;
  serialNo: string;
  plateId: string;
  medium: string;
  dilution: string;
  dilutionFactor: number;
  volumePlated: number;
  colonyCount: number | '';
  cfu: number | '';
  readingRemarks: string;
  morphology: string;
  status: 'Pending' | 'In Progress' | 'Saved';
  savedAt?: string;
  countWarning?: string;
}

interface MLTLabTestRecordingProps {
  testRecord: MLTLabTestRecord;
  onNavigate?: () => void;
  onBackToListing: () => void;
  onSaveDraft?: (updatedRecord: MLTLabTestRecord) => void;
  onSubmitForApproval?: (updatedRecord: MLTLabTestRecord) => void;
}

const DILUTION_OPTIONS: { label: string; factor: number }[] = [
  { label: '1:1 (Undiluted / Direct)', factor: 1 },
  { label: '1:10 (10⁻¹)', factor: 10 },
  { label: '1:100 (10⁻²)', factor: 100 },
  { label: '1:1,000 (10⁻³)', factor: 1000 },
  { label: '1:10,000 (10⁻⁴)', factor: 10000 },
];

export const MLTLabTestRecording: React.FC<MLTLabTestRecordingProps> = ({
  testRecord,
  onNavigate,
  onBackToListing,
  onSaveDraft,
  onSubmitForApproval,
}) => {
  const { t } = useTranslation();

  // Determine unit: solid samples (Finished Product, Raw Material) are CFU/g; liquids/water are CFU/mL
  const isSolid = testRecord.sampleType === 'Finished Product' || testRecord.sampleType === 'Raw Material';
  const cfuUnit = isSolid ? 'CFU/g' : 'CFU/mL';

  // Determine standard countable range based on test parameter
  const isTYMC = testRecord.testParameter === 'TYMC';
  const minCountable = isTYMC ? 10 : 25;
  const maxCountable = isTYMC ? 150 : 250;

  // Derive initial plates from testRecord.plateReadings or numberOfPlates
  const initialPlates: PlateObservationState[] = useMemo(() => {
    const totalPlates = testRecord.numberOfPlates || 5;
    const defaultDilution = testRecord.dilutionFactor.includes('1:100')
      ? '1:100 (10⁻²)'
      : testRecord.dilutionFactor.includes('1:10')
      ? '1:10 (10⁻¹)'
      : '1:1 (Undiluted / Direct)';
    const defaultFactor = defaultDilution.includes('1:100') ? 100 : defaultDilution.includes('1:10') ? 10 : 1;

    const plates: PlateObservationState[] = [];
    for (let i = 1; i <= totalPlates; i++) {
      const existing = testRecord.plateReadings && testRecord.plateReadings[i - 1];
      const serialNo = existing?.serialNo || `PLT-${testRecord.testParameter}-${testRecord.mltTestId.slice(-4)}-0${i}`;
      const plateId = `PLT-ID-${testRecord.mltTestId.replace(/\D/g, '').slice(-4)}-${i}`;
      
      const parsedCfu = existing && typeof existing.cfu === 'number' ? existing.cfu : '';
      const hasCfu = typeof parsedCfu === 'number';

      // Estimate initial colony count if cfu already exists
      let initialCount: number | '' = '';
      if (hasCfu) {
        initialCount = Math.round((parsedCfu as number * 1.0) / defaultFactor);
      }

      plates.push({
        plateNo: i,
        serialNo,
        plateId,
        medium: testRecord.medium,
        dilution: defaultDilution,
        dilutionFactor: defaultFactor,
        volumePlated: 1.0,
        colonyCount: initialCount,
        cfu: parsedCfu,
        readingRemarks: existing?.interimRemarks || '',
        morphology: existing?.morphology || (hasCfu ? 'Circular, smooth, cream-colored colonies' : ''),
        status: hasCfu ? 'Saved' : 'Pending',
        savedAt: hasCfu ? '10-Oct-2026 15:45' : undefined,
      });
    }
    return plates;
  }, [testRecord]);

  // State for all plates
  const [plates, setPlates] = useState<PlateObservationState[]>(initialPlates);
  const [selectedPlateIndex, setSelectedPlateIndex] = useState<number>(0);

  // General analyst remarks
  const [analystRemarks, setAnalystRemarks] = useState(
    testRecord.remarks ||
      'Incubation conditions verified (validated chamber). Negative control exhibited 0 CFU growth after required duration.'
  );

  // Active plate form state
  const currentPlate = plates[selectedPlateIndex] || plates[0];
  const [formDilution, setFormDilution] = useState(currentPlate.dilution);
  const [formVolumePlated, setFormVolumePlated] = useState<number | ''>(currentPlate.volumePlated);
  const [formColonyCount, setFormColonyCount] = useState<number | ''>(currentPlate.colonyCount);
  const [formRemarks, setFormRemarks] = useState(currentPlate.readingRemarks);
  const [formMorphology, setFormMorphology] = useState(currentPlate.morphology);
  const [formValidationError, setFormValidationError] = useState<string | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ title: string; desc: string; type: 'success' | 'info' | 'warning' } | null>(null);
  const showToast = (title: string, desc: string, type: 'success' | 'info' | 'warning' = 'success') => {
    setToast({ title, desc, type });
    setTimeout(() => setToast(null), 3800);
  };

  // Submit confirmation modal state
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [signatureAnalyst, setSignatureAnalyst] = useState('Aromal A [ Admin ]');
  const [signatureRole, setSignatureRole] = useState('Quality Control Analyst - Microbiology');
  const [signaturePassword, setSignaturePassword] = useState('');
  const [signatureError, setSignatureError] = useState<string | null>(null);
  const [isSubmittedSuccess, setIsSubmittedSuccess] = useState(false);

  // Sync form state when switching plates
  useEffect(() => {
    if (currentPlate) {
      setFormDilution(currentPlate.dilution);
      setFormVolumePlated(currentPlate.volumePlated);
      setFormColonyCount(currentPlate.colonyCount);
      setFormRemarks(currentPlate.readingRemarks);
      setFormMorphology(currentPlate.morphology);
      setFormValidationError(null);
    }
  }, [selectedPlateIndex]);

  // Derive dilution factor from selected dilution
  const currentDilutionFactor = useMemo(() => {
    const found = DILUTION_OPTIONS.find((opt) => opt.label === formDilution);
    return found ? found.factor : 10;
  }, [formDilution]);

  // Real-time automatic CFU calculation: CFU = (Colony Count * Dilution Factor) / Volume Plated
  const calculatedPlateCfu = useMemo<number | null>(() => {
    if (formColonyCount === '' || formColonyCount === null || isNaN(Number(formColonyCount))) {
      return null;
    }
    const count = Number(formColonyCount);
    if (count < 0) return null;

    const vol = Number(formVolumePlated);
    if (isNaN(vol) || vol <= 0) return null;

    // Standard plate calculation
    const cfuVal = (count * currentDilutionFactor) / vol;
    return Math.round(cfuVal);
  }, [formColonyCount, currentDilutionFactor, formVolumePlated]);

  // Real-time warning for active plate countable range
  const countWarning = useMemo(() => {
    if (formColonyCount === '' || formColonyCount === null) return null;
    const count = Number(formColonyCount);
    if (count === 0) return t('mltLabTestRecording.countWarning.noGrowth');
    if (count < minCountable) {
      return t('mltLabTestRecording.countWarning.belowRange', { min: minCountable, max: maxCountable });
    }
    if (count > maxCountable) {
      return t('mltLabTestRecording.countWarning.aboveRange', { max: maxCountable });
    }
    return t('mltLabTestRecording.countWarning.withinRange', { min: minCountable, max: maxCountable });
  }, [formColonyCount, minCountable, maxCountable, t]);

  // Overall calculations across all saved plates
  const savedPlates = useMemo(() => plates.filter((p) => p.status === 'Saved' && typeof p.cfu === 'number'), [plates]);
  const savedCount = savedPlates.length;
  const totalCount = plates.length;

  const overallAverageCfu = useMemo<number | null>(() => {
    if (savedPlates.length === 0) return null;
    const sum = savedPlates.reduce((acc, curr) => acc + (Number(curr.cfu) || 0), 0);
    return Math.round(sum / savedPlates.length);
  }, [savedPlates]);

  // Acceptance Limit threshold parser (e.g. "NMT 1000 CFU/g" -> 1000)
  const acceptanceLimitNumber = useMemo(() => {
    const match = testRecord.acceptanceLimit.match(/(\d+[\d,]*)/);
    if (match) {
      return Number(match[1].replace(/,/g, ''));
    }
    return 1000;
  }, [testRecord.acceptanceLimit]);

  // Compliance evaluation
  const evaluationResult = useMemo(() => {
    if (savedCount === 0) {
      return { status: t('mltLabTestRecording.evaluation.pendingObservations.status'), color: 'slate', text: t('mltLabTestRecording.evaluation.pendingObservations.text') };
    }
    if (savedCount < totalCount) {
      return { status: t('mltLabTestRecording.evaluation.inProgress.status'), color: 'blue', text: t('mltLabTestRecording.evaluation.inProgress.text', { saved: savedCount, total: totalCount }) };
    }
    if (overallAverageCfu !== null) {
      if (overallAverageCfu <= acceptanceLimitNumber) {
        return { status: t('mltLabTestRecording.evaluation.complies.status'), color: 'emerald', text: t('mltLabTestRecording.evaluation.complies.text', { limit: testRecord.acceptanceLimit }) };
      } else {
        return { status: t('mltLabTestRecording.evaluation.doesNotComply.status'), color: 'rose', text: t('mltLabTestRecording.evaluation.doesNotComply.text', { limit: testRecord.acceptanceLimit }) };
      }
    }
    return { status: t('mltLabTestRecording.evaluation.reviewRequired'), color: 'amber', text: t('mltLabTestRecording.evaluation.reviewRequired') };
  }, [savedCount, totalCount, overallAverageCfu, acceptanceLimitNumber, testRecord.acceptanceLimit, t]);

  // Handle Save Current Plate Details
  const handleSavePlateDetails = () => {
    // Validation
    if (formColonyCount === '' || formColonyCount === null || isNaN(Number(formColonyCount))) {
      setFormValidationError(t('mltLabTestRecording.validation.colonyCountInvalid'));
      return;
    }
    const count = Number(formColonyCount);
    if (count < 0) {
      setFormValidationError(t('mltLabTestRecording.validation.colonyCountNegative'));
      return;
    }
    const vol = Number(formVolumePlated);
    if (isNaN(vol) || vol <= 0) {
      setFormValidationError(t('mltLabTestRecording.validation.volumeInvalid'));
      return;
    }

    setFormValidationError(null);

    const cfuValue = calculatedPlateCfu ?? 0;
    const nowTimestamp = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) + ' ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    setPlates((prev) =>
      prev.map((p, idx) => {
        if (idx === selectedPlateIndex) {
          return {
            ...p,
            dilution: formDilution,
            dilutionFactor: currentDilutionFactor,
            volumePlated: vol,
            colonyCount: count,
            cfu: cfuValue,
            readingRemarks: formRemarks,
            morphology: formMorphology,
            status: 'Saved',
            savedAt: nowTimestamp,
            countWarning: countWarning || undefined,
          };
        }
        return p;
      })
    );

    showToast(
      t('mltLabTestRecording.toast.plateSaved.title', { serialNo: currentPlate.serialNo }),
      t('mltLabTestRecording.toast.plateSaved.desc', { count, cfu: cfuValue, unit: cfuUnit }),
      'success'
    );
  };

  // Handle Move to Next Plate
  const handleNextPlate = () => {
    if (selectedPlateIndex < plates.length - 1) {
      setSelectedPlateIndex(selectedPlateIndex + 1);
    }
  };

  // Handle Move to Previous Plate
  const handlePreviousPlate = () => {
    if (selectedPlateIndex > 0) {
      setSelectedPlateIndex(selectedPlateIndex - 1);
    }
  };

  // Handle Save as Draft
  const handleSaveAsDraft = () => {
    const updatedRecord: MLTLabTestRecord = {
      ...testRecord,
      status: 'In Progress',
      remarks: analystRemarks,
      plateReadings: plates.map((p) => ({
        plateNo: p.plateNo,
        serialNo: p.serialNo,
        cfu: typeof p.cfu === 'number' ? p.cfu : '—',
        morphology: p.morphology,
        interimRemarks: p.readingRemarks,
        readingTimestamp: p.savedAt,
      })),
      averageCfuPerPlate: overallAverageCfu || undefined,
      calculatedBioburden: overallAverageCfu !== null ? `${overallAverageCfu} ${cfuUnit}` : undefined,
      complianceStatus:
        overallAverageCfu !== null
          ? overallAverageCfu <= acceptanceLimitNumber
            ? 'Pass'
            : 'Fail'
          : 'Pending',
    };

    if (onSaveDraft) {
      onSaveDraft(updatedRecord);
    }
    showToast(
      t('mltLabTestRecording.toast.draftSaved.title'),
      t('mltLabTestRecording.toast.draftSaved.desc', { testId: testRecord.mltTestId }),
      'info'
    );
  };

  // Handle Submit for Lab Approval click
  const handleOpenSubmitModal = () => {
    // Check if all plates are saved
    const unsavedPlates = plates.filter((p) => p.status !== 'Saved');
    if (unsavedPlates.length > 0) {
      setToast({
        title: t('mltLabTestRecording.toast.incompletePlates.title'),
        desc: t('mltLabTestRecording.toast.incompletePlates.desc', {
          num: unsavedPlates.length,
          plates: unsavedPlates.map((p) => p.serialNo).join(', '),
        }),
        type: 'warning',
      });
      return;
    }

    setSignatureError(null);
    setShowSubmitModal(true);
  };

  // Handle Final Sign-off and Submit
  const handleFinalSubmitConfirm = () => {
    if (!signaturePassword || signaturePassword.length < 4) {
      setSignatureError(t('mltLabTestRecording.validation.signatureInvalid'));
      return;
    }

    const nowFormatted = new Date().toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) + ' ' + new Date().toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

    const updatedRecord: MLTLabTestRecord = {
      ...testRecord,
      status: 'Completed', // Or Pending Lab Approval in workflow
      remarks: analystRemarks,
      plateReadings: plates.map((p) => ({
        plateNo: p.plateNo,
        serialNo: p.serialNo,
        cfu: typeof p.cfu === 'number' ? p.cfu : '—',
        morphology: p.morphology,
        interimRemarks: p.readingRemarks,
        readingTimestamp: p.savedAt || nowFormatted,
      })),
      averageCfuPerPlate: overallAverageCfu || undefined,
      calculatedBioburden: overallAverageCfu !== null ? `${overallAverageCfu} ${cfuUnit}` : undefined,
      complianceStatus:
        overallAverageCfu !== null
          ? overallAverageCfu <= acceptanceLimitNumber
            ? 'Pass'
            : 'Fail'
          : 'Pending',
      completionDate: nowFormatted,
      analystSignOff: {
        name: signatureAnalyst,
        role: signatureRole,
        signedAt: nowFormatted,
      },
      qcReviewSignOff: {
        name: 'Pending Assignment',
        role: 'QC Lab Supervisor / QA Reviewer',
        status: 'Pending QA Review',
      },
    };

    if (onSubmitForApproval) {
      onSubmitForApproval(updatedRecord);
    }

    setIsSubmittedSuccess(true);
    setShowSubmitModal(false);
    showToast(
      t('mltLabTestRecording.toast.submitted.title'),
      t('mltLabTestRecording.toast.submitted.desc', { testId: testRecord.mltTestId }),
      'success'
    );
  };

  return (
    <div className="flex-1 flex flex-col bg-slate-50 min-h-screen media-prep-scope pb-16">
      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-16 right-8 z-50 px-4 py-3 rounded-xl shadow-lg border flex items-start gap-3 max-w-md ${
              toast.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : toast.type === 'warning'
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-blue-50 border-blue-200 text-blue-900'
            }`}
          >
            <span
              className={`material-symbols-outlined text-xl ${
                toast.type === 'success'
                  ? 'text-emerald-600'
                  : toast.type === 'warning'
                  ? 'text-amber-600'
                  : 'text-blue-600'
              }`}
            >
              {toast.type === 'success' ? 'check_circle' : toast.type === 'warning' ? 'warning' : 'info'}
            </span>
            <div className="flex-1 text-xs">
              <p className="font-bold text-sm leading-tight">{toast.title}</p>
              <p className="mt-0.5 opacity-90">{toast.desc}</p>
            </div>
            <button onClick={() => setToast(null)} className="text-slate-400 hover:text-slate-600">
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Banner / Breadcrumb & Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <nav className="flex items-center gap-2 text-xs text-slate-500 font-medium mb-1">
              <button
                // onClick={() => onNavigate()}
                className="hover:text-[#0052CC] transition-colors cursor-pointer"
              >
                {t('mltLabTestRecording.header.breadcrumbBioburden')}
              </button>
              <span className="text-slate-300">/</span>
              <button onClick={onBackToListing} className="hover:text-[#0052CC] transition-colors cursor-pointer">
                {t('mltLabTestRecording.header.breadcrumbLabTest')}
              </button>
              <span className="text-slate-300">/</span>
              <span className="text-slate-700 font-semibold">{t('mltLabTestRecording.header.title')}</span>
            </nav>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                <span className="material-symbols-outlined text-[#0052CC] text-2xl">science</span>
                {t('mltLabTestRecording.header.title')}
              </h1>
              <span className="font-mono text-sm font-bold px-2.5 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200">
                {testRecord.mltTestId}
              </span>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                  isSubmittedSuccess
                    ? 'bg-purple-50 text-purple-700 border border-purple-200'
                    : testRecord.status === 'Completed'
                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                    : testRecord.status === 'In Progress' || savedCount > 0
                    ? 'bg-blue-50 text-blue-700 border border-blue-200'
                    : 'bg-slate-100 text-slate-600 border border-slate-200'
                }`}
              >
                {isSubmittedSuccess
                  ? t('mltLabTestRecording.header.status.pendingLabApproval')
                  : testRecord.status === 'Completed'
                  ? t('mltLabTestRecording.header.status.completed')
                  : savedCount > 0
                  ? t('mltLabTestRecording.header.status.inProgress')
                  : t('mltLabTestRecording.header.status.pending')}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              {t('mltLabTestRecording.header.subtitle')}
            </p>
          </div>

          {/* Quick Header Actions */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={onBackToListing}
              className="px-3.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
            >
              <span className="material-symbols-outlined text-sm">arrow_back</span>
              {t('mltLabTestRecording.actions.backToListing')}
            </button>
            <button
              onClick={handleSaveAsDraft}
              className="px-3.5 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer shadow-xs"
            >
              <span className="material-symbols-outlined text-sm text-slate-600">save</span>
              {t('mltLabTestRecording.actions.saveAsDraft')}
            </button>
            <button
              onClick={handleOpenSubmitModal}
              disabled={isSubmittedSuccess}
              className={`px-4 py-1.5 rounded-lg text-white text-xs font-semibold flex items-center gap-1.5 transition-colors shadow-xs ${
                isSubmittedSuccess
                  ? 'bg-slate-400 cursor-not-allowed'
                  : 'bg-[#0052CC] hover:bg-[#0747A6] cursor-pointer'
              }`}
            >
              <span className="material-symbols-outlined text-sm">verified</span>
              {isSubmittedSuccess ? t('mltLabTestRecording.actions.submittedForApproval') : t('mltLabTestRecording.actions.submitForLabApproval')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl w-full mx-auto px-6 py-6 space-y-6">
        {/* ========================================================================= */}
        {/* 1. TEST DETAILS SECTION (Read-only)                                        */}
        {/* ========================================================================= */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-600 text-lg">fact_check</span>
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                {t('mltLabTestRecording.masterDetails.title')}
              </h2>
            </div>
            <span className="text-[11px] text-slate-500 font-medium">{t('mltLabTestRecording.masterDetails.subtitle')}</span>
          </div>

          <div className="p-5 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 text-xs">
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.mltTestId')}</p>
              <p className="font-mono font-bold text-slate-900 mt-0.5">{testRecord.mltTestId}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.sampleId')}</p>
              <p className="font-mono font-bold text-slate-900 mt-0.5">{testRecord.sampleId}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.productionScheduleId')}</p>
              <p className="font-mono font-bold text-[#0052CC] mt-0.5">{testRecord.productionSchedule}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.sampleType')}</p>
              <p className="font-medium text-slate-800 mt-0.5">{testRecord.sampleType}</p>
            </div>

            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.productOrMaterial')}</p>
              <p className="font-bold text-slate-900 mt-0.5">{testRecord.productOrMaterial}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.batchNo')}</p>
              <p className="font-mono font-medium text-slate-800 mt-0.5">{testRecord.batchNo}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.testParameter')}</p>
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="font-bold text-slate-900">{testRecord.testParameter}</span>
                <span className="text-[10px] px-1.5 py-0.2 rounded bg-blue-50 text-blue-700 border border-blue-200 font-semibold">
                  {testRecord.testParameter === 'TAMC'
                    ? t('mltLabTestRecording.masterDetails.parameterTag.totalAerobic')
                    : testRecord.testParameter === 'TYMC'
                    ? t('mltLabTestRecording.masterDetails.parameterTag.yeastAndMold')
                    : t('mltLabTestRecording.masterDetails.parameterTag.pathogenScreen')}
                </span>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.testMedium')}</p>
              <p className="font-medium text-slate-800 mt-0.5">{testRecord.medium}</p>
            </div>

            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.testMethod')}</p>
              <p className="font-medium text-slate-800 mt-0.5">{testRecord.testMethod}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.assignedDilution')}</p>
              <p className="font-medium text-slate-800 mt-0.5">{testRecord.dilutionFactor}</p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.incubationParameter')}</p>
              <p className="font-medium text-slate-800 mt-0.5">
                {testRecord.targetIncubationTemp} ({testRecord.incubationDuration})
              </p>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.assignedAnalyst')}</p>
              <p className="font-medium text-slate-800 mt-0.5">{testRecord.assignedAnalyst}</p>
            </div>
          </div>
        </section>

        {/* ========================================================================= */}
        {/* 2. PLATE-WISE RECORDING – VERTICAL TABS & RECORDING FORM                   */}
        {/* ========================================================================= */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          {/* Section Header with Plate Progress */}
          <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-600 text-lg">view_stream</span>
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                {t('mltLabTestRecording.plateRecording.title')}
              </h2>
            </div>

            {/* Progress Indicator */}
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-600 font-medium">
                <Trans
                  i18nKey="mltLabTestRecording.plateRecording.progress"
                  values={{ saved: savedCount, total: totalCount }}
                  components={{ strong: <strong className="text-slate-900" /> }}
                />
              </span>
              <div className="w-28 h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-300 ${
                    savedCount === totalCount ? 'bg-emerald-500' : 'bg-[#0052CC]'
                  }`}
                  style={{ width: `${(savedCount / totalCount) * 100}%` }}
                />
              </div>
              <span
                className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  savedCount === totalCount
                    ? 'bg-emerald-100 text-emerald-800'
                    : 'bg-blue-50 text-blue-700'
                }`}
              >
                {t('mltLabTestRecording.plateRecording.percentComplete', { percent: Math.round((savedCount / totalCount) * 100) })}
              </span>
            </div>
          </div>

          {/* 2-Column Vertical Tabs Layout */}
          <div className="flex flex-col lg:flex-row min-h-[460px]">
            {/* Left Side: Vertical Tabs */}
            <div className="w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-slate-200 bg-slate-50/50 p-3 space-y-2 shrink-0">
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider px-2 py-1">
                {t('mltLabTestRecording.plateRecording.assignedPlates', { total: plates.length })}
              </p>

              <div className="space-y-1.5">
                {plates.map((plate, index) => {
                  const isSelected = selectedPlateIndex === index;
                  return (
                    <button
                      key={plate.plateNo}
                      onClick={() => setSelectedPlateIndex(index)}
                      className={`w-full text-left p-3 rounded-lg border transition-all cursor-pointer relative ${
                        isSelected
                          ? 'bg-white border-[#0052CC] shadow-sm ring-1 ring-[#0052CC]/20'
                          : 'bg-white/70 hover:bg-white border-slate-200/90 hover:border-slate-300'
                      }`}
                    >
                      {/* Active Left Indicator Bar */}
                      {isSelected && (
                        <div className="absolute left-0 top-2 bottom-2 w-1 bg-[#0052CC] rounded-r" />
                      )}

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-bold text-slate-900">
                            {plate.serialNo}
                          </span>
                        </div>

                        {/* Status Badge */}
                        <span
                          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 ${
                            plate.status === 'Saved'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : plate.status === 'In Progress'
                              ? 'bg-blue-50 text-blue-700 border border-blue-200'
                              : 'bg-slate-100 text-slate-600 border border-slate-200'
                          }`}
                        >
                          {plate.status === 'Saved' && (
                            <span className="material-symbols-outlined text-[12px]">check</span>
                          )}
                          {plate.status}
                        </span>
                      </div>

                      <div className="flex items-center justify-between text-[11px] text-slate-500 mt-2">
                        <span>{t('mltLabTestRecording.plateRecording.plateNumberShort', { plateNo: plate.plateNo })}</span>
                        {plate.status === 'Saved' && typeof plate.cfu === 'number' ? (
                          <span className="font-semibold text-slate-800">
                            {/* {t('mltLabTestRecording.plateRecording.countSummary', { count: plate.colonyCount, cfu: plate.cfu, unit: cfuUnit })} */}
                          </span>
                        ) : (
                          <span className="text-slate-400 italic">{t('mltLabTestRecording.plateRecording.noCountRecorded')}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Quick instructions in left rail */}
              <div className="p-3 bg-blue-50/70 border border-blue-100 rounded-lg text-[11px] text-blue-800 mt-4 leading-relaxed">
                <div className="flex items-center gap-1.5 font-bold mb-1">
                  <span className="material-symbols-outlined text-sm">info</span>
                  {t('mltLabTestRecording.plateRecording.workflowGuidance')}
                </div>
                <Trans i18nKey="mltLabTestRecording.plateRecording.workflowGuidanceBody" components={{ strong: <strong /> }} />
              </div>
            </div>

            {/* Right Side: Plate Recording Form */}
            <div className="flex-1 p-6 flex flex-col justify-between">
              <div>
                {/* Form Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 border-b border-slate-100 gap-2 mb-6">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        {t('mltLabTestRecording.plateRecording.plateOf', { plateNo: currentPlate.plateNo, total: plates.length })}
                      </span>
                      <span className="text-slate-300">•</span>
                      <span className="font-mono text-sm font-bold text-slate-900">
                        {currentPlate.serialNo}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {t('mltLabTestRecording.plateRecording.formHint')}
                    </p>
                  </div>

                  {currentPlate.status === 'Saved' && (
                    <div className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-3 py-1 rounded-md border border-emerald-200">
                      <span className="material-symbols-outlined text-sm">check_circle</span>
                      {t('mltLabTestRecording.plateRecording.savedOn', { date: currentPlate.savedAt || t('mltLabTestRecording.plateRecording.today') })}
                    </div>
                  )}
                </div>

                {/* Validation Error Banner */}
                {formValidationError && (
                  <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-800 flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-rose-600">error</span>
                    {formValidationError}
                  </div>
                )}

                {/* Form Fields Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  {/* Field 1: Plate Serial Number (Read-only) */}
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center justify-between">
                      {t('mltLabTestRecording.plateRecording.plateSerialNumber')}
                      <span className="material-symbols-outlined text-xs text-slate-400" title={t('mltLabTestRecording.plateRecording.readOnlyField')}>
                        lock
                      </span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={currentPlate.serialNo}
                      className="w-full h-9 px-3 text-xs rounded-lg border border-slate-200 bg-slate-100 font-mono text-slate-700 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* Field 2: Plate ID (Read-only) */}
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center justify-between">
                      {t('mltLabTestRecording.plateRecording.plateIdBarcode')}
                      <span className="material-symbols-outlined text-xs text-slate-400" title={t('mltLabTestRecording.plateRecording.readOnlyField')}>
                        lock
                      </span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={currentPlate.plateId}
                      className="w-full h-9 px-3 text-xs rounded-lg border border-slate-200 bg-slate-100 font-mono text-slate-700 font-medium cursor-not-allowed"
                    />
                  </div>

                  {/* Field 3: Test Medium (Read-only) */}
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center justify-between">
                      {t('mltLabTestRecording.fields.testMedium')}
                      <span className="material-symbols-outlined text-xs text-slate-400" title={t('mltLabTestRecording.plateRecording.readOnlyField')}>
                        lock
                      </span>
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={currentPlate.medium}
                      className="w-full h-9 px-3 text-xs rounded-lg border border-slate-200 bg-slate-100 text-slate-700 font-medium cursor-not-allowed truncate"
                    />
                  </div>

                  {/* Field 4: Dilution (Dropdown) */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.plateRecording.dilution')} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formDilution}
                      onChange={(e) => setFormDilution(e.target.value)}
                      className="w-full h-9 px-3 text-xs rounded-lg border border-slate-300 bg-white text-slate-800 font-semibold focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC] cursor-pointer"
                    >
                      {DILUTION_OPTIONS.map((opt) => (
                        <option key={opt.label} value={opt.label}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <span className="text-[10px] text-slate-400 mt-0.5 block">{t('mltLabTestRecording.plateRecording.dilutionHint')}</span>
                  </div>

                  {/* Field 5: Dilution Factor (Derived, Read-only) */}
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1 flex items-center justify-between">
                      {t('mltLabTestRecording.plateRecording.dilutionFactorInverse')}
                      <span className="material-symbols-outlined text-xs text-slate-400" title={t('mltLabTestRecording.plateRecording.derivedAutomatically')}>
                        calculate
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        readOnly
                        value={currentDilutionFactor}
                        className="w-full h-9 px-3 text-xs rounded-lg border border-slate-200 bg-slate-100 font-mono font-bold text-slate-800 cursor-not-allowed"
                      />
                      <span className="absolute right-3 top-2 text-[11px] text-slate-400 font-medium">{t('mltLabTestRecording.plateRecording.factorSuffix')}</span>
                    </div>
                    <span className="text-[10px] text-slate-400 mt-0.5 block">{t('mltLabTestRecording.plateRecording.dilutionFactorHint')}</span>
                  </div>

                  {/* Field 6: Volume Plated (Required Numeric Input) */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.plateRecording.volumePlated')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={formVolumePlated}
                        onChange={(e) => setFormVolumePlated(e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="1.0"
                        className="w-full h-9 px-3 text-xs rounded-lg border border-slate-300 bg-white font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
                      />
                      <span className="absolute right-3 top-2 text-[11px] text-slate-500 font-semibold">{t('mltLabTestRecording.plateRecording.unitMl')}</span>
                    </div>
                    <span className="text-[10px] text-slate-400 mt-0.5 block">{t('mltLabTestRecording.plateRecording.volumeHint')}</span>
                  </div>

                  {/* Field 7: Colony Count (Required Integer Input) */}
                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.plateRecording.colonyCountObserved')} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={formColonyCount}
                        onChange={(e) => {
                          const val = e.target.value;
                          setFormColonyCount(val === '' ? '' : parseInt(val, 10));
                        }}
                        placeholder={t('mltLabTestRecording.plateRecording.colonyCountPlaceholder')}
                        className="w-full h-9 px-3 text-xs rounded-lg border border-slate-300 bg-white font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
                      />
                      <span className="absolute right-3 top-2 text-[11px] text-slate-500 font-semibold">{t('mltLabTestRecording.plateRecording.coloniesSuffix')}</span>
                    </div>
                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                      {t('mltLabTestRecording.plateRecording.targetRange', { min: minCountable, max: maxCountable })}
                    </span>
                  </div>

                  {/* Field 8: CFU Result (Automatically Calculated, Read-only) */}
                  <div className="md:col-span-2">
                    <label className="text-[11px] font-bold text-[#0052CC] uppercase tracking-wider mb-1 flex items-center justify-between">
                      <span>{t('mltLabTestRecording.plateRecording.calculatedPlateCfu')}</span>
                      <span className="text-[10px] font-mono text-slate-500 lowercase">
                        {t('mltLabTestRecording.plateRecording.formula')}
                      </span>
                    </label>
                    <div className="h-9 px-4 rounded-lg bg-blue-50/70 border border-blue-200 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="material-symbols-outlined text-[#0052CC] text-base">functions</span>
                        <span className="font-mono text-base font-black text-blue-900">
                          {calculatedPlateCfu !== null ? calculatedPlateCfu.toLocaleString() : '—'}
                        </span>
                        <span className="text-xs font-bold text-blue-700">{cfuUnit}</span>
                      </div>
                      <span className="text-[11px] text-blue-600/80 font-medium hidden sm:inline">
                        {calculatedPlateCfu !== null
                          ? `(${formColonyCount} × ${currentDilutionFactor}) / ${formVolumePlated} mL = ${calculatedPlateCfu} ${cfuUnit}`
                          : t('mltLabTestRecording.plateRecording.enterColonyCount')}
                      </span>
                    </div>
                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                      {t('mltLabTestRecording.plateRecording.computedHint')}
                    </span>
                  </div>

                  {/* Field 9: Colony Morphology Description */}
                  <div className="md:col-span-3">
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.plateRecording.morphologyLabel')}
                    </label>
                    <input
                      type="text"
                      value={formMorphology}
                      onChange={(e) => setFormMorphology(e.target.value)}
                      placeholder={t('mltLabTestRecording.plateRecording.morphologyPlaceholder')}
                      className="w-full h-9 px-3 text-xs rounded-lg border border-slate-300 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
                    />
                  </div>

                  {/* Field 10: Reading Remarks */}
                  <div className="md:col-span-3">
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.plateRecording.remarksLabel')}
                    </label>
                    <input
                      type="text"
                      value={formRemarks}
                      onChange={(e) => setFormRemarks(e.target.value)}
                      placeholder={t('mltLabTestRecording.plateRecording.remarksPlaceholder')}
                      className="w-full h-9 px-3 text-xs rounded-lg border border-slate-300 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
                    />
                  </div>
                </div>

                {/* Countability Range Guidance / Warning Banner */}
                {countWarning && (
                  <div
                    className={`mt-4 p-3 rounded-lg border text-xs flex items-center gap-2 ${
                      formColonyCount !== '' &&
                      (Number(formColonyCount) < minCountable || Number(formColonyCount) > maxCountable)
                        ? 'bg-amber-50 border-amber-200 text-amber-900'
                        : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    }`}
                  >
                    <span
                      className={`material-symbols-outlined text-sm ${
                        formColonyCount !== '' &&
                        (Number(formColonyCount) < minCountable || Number(formColonyCount) > maxCountable)
                          ? 'text-amber-600'
                          : 'text-emerald-600'
                      }`}
                    >
                      {formColonyCount !== '' &&
                      (Number(formColonyCount) < minCountable || Number(formColonyCount) > maxCountable)
                        ? 'warning'
                        : 'check_circle'}
                    </span>
                    <span>{countWarning}</span>
                  </div>
                )}
              </div>

              {/* Form Actions Footer */}
              <div className="pt-6 mt-6 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    onClick={handlePreviousPlate}
                    disabled={selectedPlateIndex === 0}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">chevron_left</span>
                    {t('mltLabTestRecording.plateRecording.previousPlate')}
                  </button>
                  <button
                    onClick={handleNextPlate}
                    disabled={selectedPlateIndex === plates.length - 1}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
                  >
                    {t('mltLabTestRecording.plateRecording.nextPlate')}
                    <span className="material-symbols-outlined text-sm">chevron_right</span>
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSavePlateDetails}
                    className="px-4 py-2 rounded-lg bg-[#0052CC] hover:bg-[#0747A6] text-white text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm hover:shadow cursor-pointer"
                  >
                    <span className="material-symbols-outlined text-sm">save_as</span>
                    {t('mltLabTestRecording.plateRecording.savePlateDetails')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ========================================================================= */}
        {/* 3. RESULT SUMMARY SECTION                                                  */}
        {/* ========================================================================= */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-600 text-lg">assessment</span>
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                {t('mltLabTestRecording.resultSummary.title')}
              </h2>
            </div>
            <span className="text-[11px] text-slate-500 font-medium">
              {t('mltLabTestRecording.resultSummary.subtitle')}
            </span>
          </div>

          <div className="p-5 space-y-5">
            {/* Summary KPI Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Card 1: Test Parameter */}
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.fields.testParameter')}</p>
                <p className="text-sm font-bold text-slate-900 mt-1">{testRecord.testParameter}</p>
                <p className="text-[10px] text-slate-500 mt-0.5 truncate">{testRecord.medium}</p>
              </div>

              {/* Card 2: Compendial Reference */}
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.resultSummary.specificationStandard')}</p>
                <p className="text-sm font-bold text-slate-900 mt-1">{t('mltLabTestRecording.resultSummary.specificationValue')}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{t('mltLabTestRecording.resultSummary.microbialEnumerationTests')}</p>
              </div>

              {/* Card 3: Acceptance Limit */}
              <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200">
                <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">{t('mltLabTestRecording.resultSummary.acceptanceLimit')}</p>
                <p className="text-sm font-mono font-bold text-slate-900 mt-1">{testRecord.acceptanceLimit}</p>
                <p className="text-[10px] text-slate-500 mt-0.5">{t('mltLabTestRecording.resultSummary.maxPermittedBioburden')}</p>
              </div>

              {/* Card 4: Overall Calculated Result */}
              <div className="p-3.5 rounded-xl bg-blue-50/60 border border-blue-200">
                <p className="text-[11px] font-bold text-[#0052CC] uppercase tracking-wider">
                  {t('mltLabTestRecording.resultSummary.overallCalculatedBioburden')}
                </p>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="font-mono text-lg font-black text-blue-900">
                    {overallAverageCfu !== null ? overallAverageCfu.toLocaleString() : '—'}
                  </span>
                  <span className="text-xs font-bold text-blue-800">{cfuUnit}</span>
                </div>
                <p className="text-[10px] text-blue-600 mt-0.5">
                  {savedCount > 0
                    ? t('mltLabTestRecording.resultSummary.meanOfSavedPlates', { count: savedCount })
                    : t('mltLabTestRecording.resultSummary.awaitingPlateSaves')}
                </p>
              </div>

              {/* Card 5: Preliminary Evaluation */}
              <div
                className={`p-3.5 rounded-xl border ${
                  evaluationResult.color === 'emerald'
                    ? 'bg-emerald-50 border-emerald-200'
                    : evaluationResult.color === 'rose'
                    ? 'bg-rose-50 border-rose-200'
                    : evaluationResult.color === 'blue'
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-slate-50 border-slate-200'
                }`}
              >
                <p
                  className={`text-[11px] font-bold uppercase tracking-wider ${
                    evaluationResult.color === 'emerald'
                      ? 'text-emerald-700'
                      : evaluationResult.color === 'rose'
                      ? 'text-rose-700'
                      : evaluationResult.color === 'blue'
                      ? 'text-blue-700'
                      : 'text-slate-600'
                  }`}
                >
                  {t('mltLabTestRecording.resultSummary.preliminaryEvaluation')}
                </p>
                <div className="flex items-center gap-1.5 mt-1">
                  <span
                    className={`material-symbols-outlined text-base ${
                      evaluationResult.color === 'emerald'
                        ? 'text-emerald-600'
                        : evaluationResult.color === 'rose'
                        ? 'text-rose-600'
                        : 'text-slate-500'
                    }`}
                  >
                    {evaluationResult.color === 'emerald'
                      ? 'verified'
                      : evaluationResult.color === 'rose'
                      ? 'cancel'
                      : 'hourglass_empty'}
                  </span>
                  <span
                    className={`text-sm font-bold ${
                      evaluationResult.color === 'emerald'
                        ? 'text-emerald-900'
                        : evaluationResult.color === 'rose'
                        ? 'text-rose-900'
                        : 'text-slate-800'
                    }`}
                  >
                    {evaluationResult.status}
                  </span>
                </div>
                <p
                  className={`text-[10px] mt-0.5 truncate ${
                    evaluationResult.color === 'emerald'
                      ? 'text-emerald-700'
                      : evaluationResult.color === 'rose'
                      ? 'text-rose-700'
                      : 'text-slate-500'
                  }`}
                >
                  {evaluationResult.text}
                </p>
              </div>
            </div>

            {/* Negative & Positive Controls Verification Bar */}
            <div className="p-3.5 rounded-lg bg-slate-50 border border-slate-200 flex flex-col md:flex-row md:items-center justify-between text-xs text-slate-700 gap-3">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600 text-sm">check_circle</span>
                  <span>
                    <Trans
                      i18nKey="mltLabTestRecording.resultSummary.negativeControlPlate"
                      values={{ cfu: testRecord.negativeControlCfu }}
                      components={{ strong: <strong /> }}
                    />
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-600 text-sm">check_circle</span>
                  <span>
                    <Trans i18nKey="mltLabTestRecording.resultSummary.positiveGrowthPromotion" components={{ strong: <strong /> }} />
                  </span>
                </div>
              </div>
              <div className="text-[11px] text-slate-500">
                <Trans
                  i18nKey="mltLabTestRecording.resultSummary.testMethodSamplePrep"
                  values={{ method: testRecord.testMethod, prep: testRecord.dilutionFactor }}
                  components={{ strong: <strong /> }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ========================================================================= */}
        {/* 4. REMARKS & ACTIONS SECTION                                               */}
        {/* ========================================================================= */}
        <section className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
          <div className="px-5 py-3.5 bg-slate-50/80 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-slate-600 text-lg">edit_note</span>
              <h2 className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                {t('mltLabTestRecording.finalActions.title')}
              </h2>
            </div>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                {t('mltLabTestRecording.finalActions.remarksLabel')}
              </label>
              <textarea
                rows={3}
                value={analystRemarks}
                onChange={(e) => setAnalystRemarks(e.target.value)}
                placeholder={t('mltLabTestRecording.finalActions.remarksPlaceholder')}
                className="w-full p-3 text-xs rounded-lg border border-slate-300 bg-white text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
              />
            </div>

            {/* Bottom Actions Bar */}
            <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="material-symbols-outlined text-sm text-slate-400">person</span>
                <span>
                  <Trans
                    i18nKey="mltLabTestRecording.finalActions.currentAnalyst"
                    values={{ analyst: 'Aromal A [ Admin ]' }}
                    components={{ strong: <strong className="text-slate-800" /> }}
                  />
                </span>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <button
                  onClick={onBackToListing}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold transition-colors cursor-pointer shadow-xs"
                >
                  {t('mltLabTestRecording.actions.backToListing')}
                </button>
                <button
                  onClick={handleSaveAsDraft}
                  className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors cursor-pointer shadow-xs"
                >
                  <span className="material-symbols-outlined text-sm text-slate-600">save</span>
                  {t('mltLabTestRecording.actions.saveAsDraft')}
                </button>
                <button
                  onClick={handleOpenSubmitModal}
                  disabled={isSubmittedSuccess}
                  className={`flex-1 sm:flex-none px-5 py-2 rounded-lg text-white text-xs font-bold flex items-center justify-center gap-1.5 transition-all shadow-sm ${
                    isSubmittedSuccess
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-[#0052CC] hover:bg-[#0747A6] cursor-pointer'
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">verified</span>
                  {isSubmittedSuccess ? t('mltLabTestRecording.actions.submittedForApproval') : t('mltLabTestRecording.actions.submitForLabApproval')}
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* ========================================================================= */}
      {/* 5. ELECTRONIC SIGNATURE / SUBMIT FOR LAB APPROVAL MODAL                    */}
      {/* ========================================================================= */}
      <AnimatePresence>
        {showSubmitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-xs p-4">
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-lg w-full overflow-hidden"
            >
              {/* Modal Header */}
              <div className="px-6 py-4 bg-[#0F2942] text-white flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-emerald-400 text-xl">verified_user</span>
                  <h3 className="font-bold text-sm tracking-wide">
                    {t('mltLabTestRecording.signatureModal.title')}
                  </h3>
                </div>
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-4 text-xs">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-blue-900 space-y-1">
                  <p className="font-bold text-sm">{t('mltLabTestRecording.signatureModal.submitTest', { testId: testRecord.mltTestId })}</p>
                  <p className="text-xs">
                    {t('mltLabTestRecording.signatureModal.declaration', { total: plates.length })}
                  </p>
                </div>

                {/* Test Summary Table */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-1.5">
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('mltLabTestRecording.signatureModal.testParameter')}</span>
                    <span className="font-bold text-slate-800">{testRecord.testParameter}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('mltLabTestRecording.signatureModal.totalPlatesRecorded')}</span>
                    <span className="font-bold text-slate-800">{t('mltLabTestRecording.signatureModal.platesCount', { total: plates.length })}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('mltLabTestRecording.signatureModal.calculatedMeanBioburden')}</span>
                    <span className="font-bold text-blue-700">
                      {overallAverageCfu !== null ? `${overallAverageCfu} ${cfuUnit}` : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('mltLabTestRecording.signatureModal.acceptanceLimit')}</span>
                    <span className="font-bold text-slate-800">{testRecord.acceptanceLimit}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('mltLabTestRecording.signatureModal.preliminaryStatus')}</span>
                    <span
                      className={`font-bold ${
                        evaluationResult.color === 'emerald'
                          ? 'text-emerald-700'
                          : evaluationResult.color === 'rose'
                          ? 'text-rose-700'
                          : 'text-amber-700'
                      }`}
                    >
                      {evaluationResult.status}
                    </span>
                  </div>
                </div>

                {signatureError && (
                  <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-lg text-rose-800 flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm text-rose-600">error</span>
                    {signatureError}
                  </div>
                )}

                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.signatureModal.signingUserName')}
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={signatureAnalyst}
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-slate-100 font-semibold text-slate-700 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-600 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.signatureModal.roleResponsibility')}
                    </label>
                    <input
                      type="text"
                      readOnly
                      value={signatureRole}
                      className="w-full h-9 px-3 rounded-lg border border-slate-200 bg-slate-100 text-slate-700 cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold text-slate-700 uppercase tracking-wider mb-1">
                      {t('mltLabTestRecording.signatureModal.signaturePin')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      placeholder={t('mltLabTestRecording.signatureModal.passwordPlaceholder')}
                      value={signaturePassword}
                      onChange={(e) => setSignaturePassword(e.target.value)}
                      className="w-full h-9 px-3 rounded-lg border border-slate-300 bg-white font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0052CC]/20 focus:border-[#0052CC]"
                    />
                    <span className="text-[10px] text-slate-400 mt-0.5 block">
                      {t('mltLabTestRecording.signatureModal.complianceHint')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowSubmitModal(false)}
                  className="px-4 py-2 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold cursor-pointer"
                >
                  {t('mltLabTestRecording.signatureModal.cancel')}
                </button>
                <button
                  onClick={handleFinalSubmitConfirm}
                  className="px-5 py-2 rounded-lg bg-[#0052CC] hover:bg-[#0747A6] text-white text-xs font-bold flex items-center gap-1.5 shadow-sm cursor-pointer"
                >
                  <span className="material-symbols-outlined text-sm">verified</span>
                  {t('mltLabTestRecording.signatureModal.signAndSubmit')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MLTLabTestRecording;